import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { formatLocation } from '../../../utils/formatters';
import { 
  CalendarIcon, 
  TicketIcon, 
  HeartIcon, 
  UserCircleIcon,
  MapPinIcon,
  ArrowRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  StarIcon,
  ArrowPathIcon,
  MapIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const DashboardHome = () => {
  const { user: authUser } = useAuth();
  const [showError, setShowError] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Fallback images for different categories
  const getFallbackImage = (category) => {
    const fallbacks = {
      'Wildlife Safari': 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Beach Vacation': 'https://images.unsplash.com/photo-1520454974749-611b7248ffdb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Mountain Climbing': 'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Adventure': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Cultural Experience': 'https://images.unsplash.com/photo-1489493585363-d69421e0edd3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'City Tour': 'https://images.unsplash.com/photo-1611348524140-53c9a25ad10a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'default': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80'
    };
    
    if (!category) return fallbacks.default;
    
    // If we have categories as an array, use the first one
    const categoryName = Array.isArray(category) && category.length > 0 
      ? category[0].name 
      : (typeof category === 'string' ? category : 'default');
      
    return fallbacks[categoryName] || fallbacks.default;
  };
  
  // Get image URL
  const getImageUrl = (tour) => {
    if (imageErrors[tour.id]) return getFallbackImage(tour.category);
    
    // If image is a full URL
    if (tour.image && (tour.image.startsWith('http://') || tour.image.startsWith('https://'))) {
      return tour.image;
    }
    
    // If image is a relative path
    if (tour.image) {
      return `${apiUrl}/storage/${tour.image}`;
    }
    
    // Try featured_image if image is not available
    if (tour.featured_image) {
      if (tour.featured_image.startsWith('http://') || tour.featured_image.startsWith('https://')) {
        return tour.featured_image;
      }
      return `${apiUrl}/storage/${tour.featured_image}`;
    }
    
    return getFallbackImage(tour.category);
  };
  
  // Fetch user data
  const fetchUserData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const api = axios.create({
        baseURL: apiUrl
      });
      const response = await api.get('/api/v1/user/dashboard', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  };
  
  // User data query
  const { 
    data: userData, 
    isLoading, 
    error,
    refetch 
  } = useQuery('userDashboard', fetchUserData, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    onError: () => {
      setShowError(true);
    }
  });

  const handleTryAgain = () => {
    setShowError(false);
    refetch();
  };

  // If there's an error
  if (showError) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-8 rounded-md text-center">
        <div className="flex flex-col items-center justify-center">
          <div className="rounded-full bg-red-100 p-3 mb-4">
            <svg className="h-12 w-12 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-red-700 mb-2">Something went wrong</h2>
          <p className="text-red-600 mb-6">Failed to load dashboard data. Please try again later.</p>
          <div className="flex space-x-4">
            <button 
              onClick={handleTryAgain}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              <ArrowPathIcon className="w-5 h-5 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Merge auth user data with dashboard data
  const displayUser = {
    ...userData?.data?.user,
    name: authUser?.name || userData?.data?.user?.name,
    email: authUser?.email || userData?.data?.user?.email
  };

  return (
    <div className="space-y-6">
      {/* Welcome section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <img
              className="h-16 w-16 rounded-full object-cover border-2 border-gray-200"
              src={displayUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayUser.name)}&background=0D8ABC&color=fff`}
              alt={displayUser.name}
            />
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-bold text-gray-800">Welcome back, {displayUser.name}!</h2>
            <p className="text-sm text-gray-500">{displayUser.email}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <CalendarIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Bookings</h3>
              <p className="text-2xl font-semibold text-gray-800">{userData?.data?.stats?.totalBookings || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <MapIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Upcoming Tours</h3>
              <p className="text-2xl font-semibold text-gray-800">{userData?.data?.stats?.upcomingTours || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Completed Tours</h3>
              <p className="text-2xl font-semibold text-gray-800">{userData?.data?.stats?.completedTours || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <HeartIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Saved Tours</h3>
              <p className="text-2xl font-semibold text-gray-800">{userData?.data?.stats?.savedTours || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Bookings */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">Upcoming Bookings</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {userData?.data?.upcomingBookings && userData?.data?.upcomingBookings.length > 0 ? (
            userData.data.upcomingBookings.map((booking) => (
              <div key={booking.id} className="p-6">
                <div className="flex flex-col md:flex-row md:items-center">
                  <div className="flex-shrink-0 mb-4 md:mb-0">
                    <img
                      src={booking.tourImage && (booking.tourImage.startsWith('http://') || booking.tourImage.startsWith('https://')) 
                        ? booking.tourImage 
                        : booking.tourImage 
                          ? `${apiUrl}/storage/${booking.tourImage}` 
                          : getFallbackImage('default')}
                      alt={booking.tourTitle}
                      className="h-24 w-32 object-cover rounded-md"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = getFallbackImage('default');
                      }}
                    />
                  </div>
                  <div className="md:ml-6 flex-grow">
                    <h4 className="text-lg font-medium text-gray-900">{booking.tourTitle}</h4>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <MapPinIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                      <p>{booking.tourLocation}</p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <CalendarIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                      <p>Booking Date: {new Date(booking.bookingDate).toLocaleDateString()}</p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <UserCircleIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                      <p>{booking.numTravelers} Travelers</p>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0 md:ml-6">
                    <div className="flex flex-col items-end">
                      <span className="text-lg font-medium text-gray-900">${booking.totalAmount}</span>
                      <span className={`mt-1 px-2 py-1 text-xs font-medium rounded-full ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </span>
                    </div>
                    <Link
                      to={`/dashboard/bookings/${booking.id}`}
                      className="mt-3 flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-500">You don't have any upcoming bookings.</p>
              <Link
                to="/tours"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
              >
                Browse Tours
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Recently Viewed Tours */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">Recently Viewed Tours</h3>
          <Link to="/tours" className="text-primary hover:text-primary-dark text-sm font-medium flex items-center">
            View All Tours
            <ArrowRightIcon className="ml-1 h-4 w-4" />
          </Link>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {userData?.data?.recentlyViewedTours && userData?.data?.recentlyViewedTours.length > 0 ? (
            userData.data.recentlyViewedTours.map((tour) => (
              <div key={tour.id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <img
                  src={getImageUrl(tour)}
                  alt={tour.title}
                  className="h-48 w-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = getFallbackImage(tour.category);
                  }}
                />
                <div className="p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-1">{tour.title}</h4>
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <MapPinIcon className="flex-shrink-0 mr-1 h-4 w-4 text-gray-400" />
                    <p>{formatLocation(tour.location)}</p>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center text-sm text-gray-500">
                      <ClockIcon className="flex-shrink-0 mr-1 h-4 w-4 text-gray-400" />
                      <p>{tour.duration} days</p>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <StarIcon className="flex-shrink-0 mr-1 h-4 w-4 text-yellow-400" />
                      <p>{tour.rating}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="flex-shrink-0 h-5 w-5 text-gray-500" />
                      <span className="ml-1 text-lg font-semibold text-gray-900">{tour.price}</span>
                      <span className="text-sm text-gray-500 ml-1">per person</span>
                    </div>
                    <Link
                      to={`/tours/${tour.id}`}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-8">
              <p className="text-gray-500">No recently viewed tours.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
